import { Popover as BootstrapPopover, PopoverProps as BootstrapPopoverProps, Overlay, OverlayProps } from 'react-bootstrap';
import { ForwardedRef, forwardRef, useMemo } from 'react';

import Button from '@/common/Button';
import { getClasses } from '@/utils';
import useUuid from '@/hooks/useUuid';

export namespace PopoverDefinitions {
  export type Props = {
    target: string | HTMLElement;
    onHide?: () => void;
    content?: () => JSX.Element;
    showCloseButton?: boolean;
  } & BootstrapPopoverProps &
    Omit<OverlayProps, 'as' | 'target' | 'container' | 'onHide' | 'content' | 'children'>;
}

const Popover = forwardRef(
  (
    {
      target,
      onHide,
      children,
      className,
      show,
      placement,
      rootClose,
      transition,
      popperConfig,
      offset,
      flip,
      showCloseButton = true,
      role = 'tooltip',
    }: PopoverDefinitions.Props,
    ref: ForwardedRef<HTMLDivElement>
  ): JSX.Element => {
    const uuid = useUuid();
    const targetElement = useMemo(() => {
      if (!target) return undefined;
      if (typeof target === 'string') {
        const element = document.querySelector(target);
        if (element) return element as HTMLElement;
        console.warn(`Target element with selector "${target}" not found.`);
        return undefined;
      } else if (target instanceof HTMLElement) {
        return target;
      } else {
        console.warn('Invalid target selector provided. Expected a string or HTMLElement.');
        return undefined;
      }
    }, [target]);

    if (!target || !targetElement) return null;
    return (
      <Overlay
        show={show}
        onHide={onHide}
        placement={placement}
        rootClose={rootClose}
        transition={transition}
        popperConfig={popperConfig}
        offset={offset}
        flip={flip}
        target={targetElement}
        container={document.body}
        ref={ref}
      >
        <BootstrapPopover id={uuid} className={getClasses('Popup Popup-Popover', className)} role={role}>
          {onHide && showCloseButton !== undefined && showCloseButton !== false && (
            <Button className="CloseButton Popover-Close" variant="icon" onClick={onHide}>
              <i className="sv sv-cross" />
            </Button>
          )}
          <BootstrapPopover.Body>{children}</BootstrapPopover.Body>
        </BootstrapPopover>
      </Overlay>
    );
  }
);
Popover.displayName = 'Popover';

export default Popover;
